import { useTranslation } from 'react-i18next';

import FullscreenExitLine from '@/assets/icons/fullscreen_exit_line.svg?component';
import FullscreenLine from '@/assets/icons/fullscreen_line.svg?component';
import LayerLine from '@/assets/icons/layer_line.svg?component';
import ZoomInLine from '@/assets/icons/zoom_in_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';

import type { Image, ImageFeedback, ImageViewerOptions } from '../../types';
import type { VariantBehaviour } from '../../variants';
import DownloadImages from '../DownloadImages/DownloadImages';
import HelpDropdown from '../HelpDropdown/HelpDropdown';
import ShareFalseDetection from '../ShareFalseDetection/ShareFalseDetection';
import ShareImage from '../ShareImage/ShareImage';

import ImageToolbar from './ImageToolbar';

import styles from './ViewerToolbar.module.scss';

export interface ViewerToolbarProps {
    images: Image[];
    imageIndex: number;
    currentImage: Image | null;
    behaviour: VariantBehaviour;
    options: Required<Pick<ImageViewerOptions, 'boxes' | 'magnifierEnabled' | 'showHelp'>>;
    isOptoScaleAdmin: boolean;
    /** After the B&W override, not the raw prop: the box toggle is colour-only. */
    isColor: boolean;
    showBoxes: boolean;
    onShowBoxesChange: (show: boolean) => void;
    magnifierOn: boolean;
    onMagnifierChange: (on: boolean) => void;
    isFullScreen: boolean;
    onToggleFullScreen: () => void;
    onCommitFeedback: (dto: ImageFeedback) => void;
}

/**
 * The controls above the image.
 *
 * Split out of ImageViewer so that the container reads as what it orchestrates rather than
 * as one long tree. The wrapper <div> around each control is required, not decoration:
 * ImageToolbar.module.scss scopes the whole button skin `> div button`.
 */
export default function ViewerToolbar(props: ViewerToolbarProps) {
    const {
        images,
        imageIndex,
        currentImage,
        behaviour,
        options,
        isOptoScaleAdmin,
        isColor,
        showBoxes,
        onShowBoxesChange,
        magnifierOn,
        onMagnifierChange,
        isFullScreen,
        onToggleFullScreen,
        onCommitFeedback,
    } = props;

    const { t } = useTranslation();

    return (
        <ImageToolbar>
            <div className={styles.removeOnSmallScreen}>
                {images.length > 0 && (
                    <ShareFalseDetection
                        isOptoScaleAdmin={isOptoScaleAdmin}
                        onCommitFalseDetection={onCommitFeedback}
                        showLiceOptions={behaviour.feedbackOptions === 'lice'}
                        showHealthOptions={behaviour.feedbackOptions === 'health'}
                        showLaksvelOptions={behaviour.feedbackOptions === 'laksvel'}
                    />
                )}
            </div>
            <div className={styles.removeOnSmallScreen}>
                <ShareImage
                    isOptoScaleAdmin={isOptoScaleAdmin}
                    permalink={currentImage?.permalink ?? ''}
                    scpCommand={currentImage?.scpCommand ?? ''}
                />
            </div>
            <div className={styles.removeOnSmallScreen}>{options.showHelp && <HelpDropdown />}</div>
            <div className={styles.removeOnSmallScreen}>
                {isColor && options.boxes !== 'hidden' && (
                    <Tooltip content={t('organisms.imageViewer.toggleFishHealthMetaLabels')}>
                        <Button
                            variant="toolbar"
                            size="small"
                            name="pois"
                            pressed={showBoxes}
                            trackingId="image_viewer_toggle_pois"
                            onClick={() => onShowBoxesChange(!showBoxes)}
                            iconOnly
                            aria-label={t('organisms.imageViewer.toggleFishHealthMetaLabels')}
                            icon={<SvgIcon icon={LayerLine} />}
                        />
                    </Tooltip>
                )}
            </div>
            <div>
                {options.magnifierEnabled && (
                    <Tooltip content={t('organisms.imageViewer.toggleInspectionMagnifyer')}>
                        <Button
                            variant="toolbar"
                            size="small"
                            name="magnifyer"
                            pressed={magnifierOn}
                            trackingId="image_viewer_toggle_magnifyer"
                            onClick={() => onMagnifierChange(!magnifierOn)}
                            iconOnly
                            aria-label={t('organisms.imageViewer.toggleInspectionMagnifyer')}
                            icon={<SvgIcon icon={ZoomInLine} />}
                        />
                    </Tooltip>
                )}
            </div>
            <div className={styles.removeOnSmallScreen}>
                <DownloadImages imageList={images} imageIndex={imageIndex} />
            </div>
            <div className={styles.removeOnSmallScreen}>
                <Tooltip content={t('organisms.imageViewer.showImageViewerInFull')}>
                    <Button
                        name="fullscreen"
                        type="button"
                        size={'small'}
                        variant="toolbar"
                        iconOnly
                        aria-label={t('organisms.imageViewer.showImageViewerInFull')}
                        pressed={isFullScreen}
                        onClick={onToggleFullScreen}
                        icon={
                            isFullScreen ? (
                                <SvgIcon icon={FullscreenExitLine} />
                            ) : (
                                <SvgIcon icon={FullscreenLine} />
                            )
                        }
                    />
                </Tooltip>
            </div>
        </ImageToolbar>
    );
}
